/**
 * CF-BONUS-SETTLED-UNAVAILABLE-RESILIENCE-V1 — the single owner of "does this player have a bonus?".
 *
 * Authored 2026-08-19 after PL Telegram #13483 (04:46:24Z), installed under Scorer's local
 * BUILD/TEST SIGN (04:50Z). Governing source: cumulative R1 `c5fd07bb…` + ACKed R2 overlay
 * `d0bc2817…`, against baseline `a8b836138a3c06ffe2d5a2e98abea5131cb0fd1f`.
 *
 * WHY THIS EXISTS: several call sites used to ask `/api/v1/bonus` independently, and a failed read
 * rendered as nothing. To a player holding chips, nothing is indistinguishable from "you have no
 * bonus" — their money appearing to vanish. This module makes that question single-owner, so an
 * unknown answer is *published as unknown* instead of silently becoming an absence.
 *
 * Pure w.r.t. the platform: no React, no `window`, no direct `setTimeout`. The clock, timers and
 * transport are injected, so the whole 19-state algebra is exercisable in plain Node.
 *
 * ⛔ NOT in this module, by ruling: display-validity's `reserveBonusIntent`/origin-token addendum,
 * cash stamping, request deadlines, or any stale-`none` repair. This ticket does NOT close the
 * historical visibility incident.
 */

// Explicit `.ts` extensions: the repo's testable-module convention (`withdraw-bonus-guard.ts:18-19`),
// enabled by `allowImportingTsExtensions` in `frontend/tsconfig.json:9`. Extensionless imports
// resolve under Vite but not under Node's ESM loader, which is what the suites run on.
import type { BonusView, ReadOutcome, UnavailableView } from './bonus-state.ts';
import {
  classifyRead, isAutomaticallyRetryable, nextAutomaticDelayMs,
  EVENT_MIN_INTERVAL_MS, MAX_AUTOMATIC_ATTEMPTS,
  type ReadClass, type ReadObservation,
} from './bonus-retry-policy.ts';

/**
 * Why a read was asked for.
 *
 * ⛔ These are TWO types, not one union with a runtime membership test. A single `BonusReason`
 * parameter on `notifyEvent()` made `notifyEvent('activate')` type-correct, and `dueAt()` then
 * classified that stored external intent as lifecycle and returned `-Infinity` — an uncooldowned
 * request through the event door, with no cast and no runtime error. Splitting the types makes the
 * bypass unconstructible instead of merely unused, so "every event probe obeys the floor" is a
 * property of the boundary rather than a convention its callers happen to respect.
 */
export type BonusLifecycleReason = 'base_change' | 'activate' | 'session_ready';
export type BonusEventReason =
  | 'deposit_credited' | 'flip_settled' | 'withdrawal_queued'
  | 'withdrawal_completed' | 'failed_flip_refunded' | 'foreground';
export type BonusReason = BonusLifecycleReason | BonusEventReason;

/** What a physical start is FOR. Cooldown is a property of this, never of a reason string. */
type IntentKind = 'establishment' | 'automatic' | 'event';

/**
 * ACTIVE_INTENT := { establishing, awaiting_start } — a current-generation intent exists.
 * `establishing` has physically started; `awaiting_start` is stored, waiting on an old settle
 * and/or the cooldown. Nothing else distinguishes them, so they admit identical latch/view pairs.
 */
export type BonusPhase =
  | 'awaiting_session' | 'establishing' | 'awaiting_start'
  | 'retry_scheduled' | 'settled' | 'exhausted';

/** One atomic publication. The panel's visibility predicate reads only these three fields. */
export type BonusSnapshot = {
  phase: BonusPhase;
  view: BonusView;
  bootstrapped: boolean;
  initialReadExhausted: boolean;
  /** Generation + sequence so a consumer can reject a stale paint. */
  eventGen: number;
  seq: number;
};

/**
 * ⭐ The visibility rule — one derived predicate, and it is the reason an exhausted card cannot
 * vanish while its recovery probe is in flight (`phase` is deliberately not consulted).
 */
export function isVisible(s: Pick<BonusSnapshot, 'bootstrapped' | 'initialReadExhausted'>): boolean {
  return s.bootstrapped || s.initialReadExhausted;
}

export type TimerHandle = unknown;

export type CoordinatorDeps = {
  /** One physical read. Single attempt: no delay, no retry, no backoff inside it. */
  /**
   * ⛔ DISCRIMINATED on purpose: a success carries a REAL view, a failure carries an UNAVAILABLE
   * one. The previous `{observation, view: BonusView}` made `{observation:{kind:'network'},
   * view: <ACTIVE>}` constructible, and the coordinator would emit
   * `{phase:'retry_scheduled', bootstrapped:false, view:active}` — illegal under R5 I1/I3, and the
   * panel would render an unbootstrapped real view. The production transport never built that pair,
   * but the design made it IMPOSSIBLE rather than merely unbuilt.
   */
  readOnce: (apiBase: string) => Promise<ReadOutcome>;
  now: () => number;
  setTimer: (fn: () => void, ms: number) => TimerHandle;
  clearTimer: (h: TimerHandle) => void;
  publish: (snapshot: BonusSnapshot) => void;
};

// ⛔ Typed UnavailableView, not BonusView. The discriminated outcome rejected the wider type on
// first typecheck — a failure arm may not carry a real view, and the compiler said so.
const UNAVAILABLE_NETWORK: UnavailableView = { kind: 'unavailable', reason: 'network' };

export class BonusReadCoordinator {
  private readonly d: CoordinatorDeps;

  private apiBase: string | null = null;
  private sessionReady = false;
  private active = false;

  private phase: BonusPhase = 'awaiting_session';
  private view: BonusView = UNAVAILABLE_NETWORK;
  private bootstrapped = false;
  private initialReadExhausted = false;

  private currentGen = 0;
  private currentSeq = 0;
  /** AUTOMATIC reads STARTED this generation. ⛔ Counted at actual start, never on failed settle. */
  private automaticAttempts = 0;
  /** Scheduled automatic slots CANCELLED by an event acceleration. Consumed, never refunded. */
  private automaticSlotsSpent = 0;
  /** Event-driven probes started. ⛔ These must NOT consume `automaticAttempts`. */
  private eventProbes = 0;
  /** The kind of the request currently in flight, carried through to settle. */
  private inFlightKind: IntentKind | null = null;
  private inFlight = false;

  /**
   * The lifecycle intent, TAGGED with the kind of read it will produce.
   * ⛔ `establishment` and `automatic` both go through this slot but are DIFFERENT request kinds and
   * spend different budgets — collapsing them is what let a failed EVENT probe eat the automatic
   * ladder. R5 requires three separate mechanics.
   */
  private pendingEstablish: { gen: number; kind: 'establishment' | 'automatic' } | null = null;
  /**
   * The newest event intent, STAMPED with the generation and sequence it was raised under.
   * ⛔ The stamp is load-bearing: without it a cooldown callback surviving a base change has no way
   * to reject itself, and fires an old-generation event against the new base.
   */
  private pendingExternal: { gen: number; seq: number; reason: BonusEventReason } | null = null;

  private retryTimer: TimerHandle | null = null;
  private cooldownTimer: TimerHandle | null = null;
  private lastOwnerRequestStart = -Infinity;

  constructor(deps: CoordinatorDeps) { this.d = deps; }

  // ── Snapshot ───────────────────────────────────────────────────────────────

  /** Budget taxonomy, exposed so the frozen counter semantics are assertable rather than implied. */
  counters(): {
    automaticAttempts: number; automaticSlotsSpent: number; eventProbes: number;
    /** The authoritative ladder index: starts + cancellations. Exposed so a test asserts the
     *  quantity the CODE decides with, not a proxy that could drift from it. */
    rungsConsumed: number;
  } {
    return {
      automaticAttempts: this.automaticAttempts,
      automaticSlotsSpent: this.automaticSlotsSpent,
      eventProbes: this.eventProbes,
      rungsConsumed: this.rungsConsumed(),
    };
  }

  snapshot(): BonusSnapshot {
    return {
      phase: this.phase, view: this.view,
      bootstrapped: this.bootstrapped, initialReadExhausted: this.initialReadExhausted,
      eventGen: this.currentGen, seq: this.currentSeq,
    };
  }

  private emit(): void {
    // Row 1: a deactivated owner publishes nothing at all.
    if (!this.active) return;
    this.d.publish(this.snapshot());
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * ⛔ `activate()` CREATES the lifecycle intent — it does not look for a stored one.
   *
   * R1 cleared both intents on deactivate and then dispatched on activate only "if an intent is
   * stored". Immediately after the required cleanup none is, so a StrictMode setup→cleanup→setup
   * left the old reply stale, queued no replacement, and parked the owner forever — recreating the
   * permanent blank through the very path the cleanup exists to prove safe.
   */
  activate(): void {
    if (this.active) return;
    this.active = true;

    // Pre-session: stay silent, issue nothing. ZERO physical requests before session_ready.
    if (!this.sessionReady || this.apiBase === null) {
      this.phase = 'awaiting_session';
      this.emit();
      return;
    }

    if (this.inFlight) {
      // An old physical request is still alive: invalidate it and queue exactly one replacement,
      // started when that old request settles (row 2). Never overlap.
      this.currentGen += 1;
      this.pendingEstablish = { gen: this.currentGen, kind: 'establishment' };
      this.phase = 'awaiting_start';
      this.emit();
      return;
    }

    // Settled / exhausted / cancelled-retry idle: create exactly one lifecycle intent.
    // ⛔ Do NOT rearm a retry timer cancelled by the deactivation, and do NOT reset the spent
    // automatic budget — recovery from exhaustion is one shot.
    this.pendingEstablish = { gen: this.currentGen, kind: 'establishment' };
    this.phase = 'awaiting_start';
    this.emit();
    this.maybeStart();
  }

  /**
   * Cancel both timers, invalidate queued callbacks, clear both intents.
   * ⛔ `bootstrapped` and `initialReadExhausted` SURVIVE — only a base change or a genuinely new
   * instance clears them, so a visible exhaustion card does not flicker off under StrictMode.
   */
  deactivate(): void {
    this.active = false;
    this.clearRetryTimer();
    this.clearCooldownTimer();
    this.pendingEstablish = null;
    this.pendingExternal = null;
  }

  setBase(apiBase: string): void {
    if (this.apiBase === apiBase) return;
    this.apiBase = apiBase;
    this.currentGen += 1;
    // A different backend is a different world: the latches are about THIS base's answers.
    this.bootstrapped = false;
    this.initialReadExhausted = false;
    this.automaticAttempts = 0;
    this.automaticSlotsSpent = 0;
    this.eventProbes = 0;
    this.inFlightKind = null;
    this.view = UNAVAILABLE_NETWORK;
    // ⛔ BOTH timers and the stamped external intent. Clearing only `retryTimer` left an armed
    // cooldown alive across the reset: an OLD-generation event fired against the NEW base and issued
    // a third request where the contract allows two. The intent must go with the world it belonged to.
    this.clearRetryTimer();
    this.clearCooldownTimer();
    this.pendingExternal = null;
    this.pendingEstablish = { gen: this.currentGen, kind: 'establishment' };
    this.phase = this.sessionReady ? 'awaiting_start' : 'awaiting_session';
    this.emit();
    this.maybeStart();
  }

  setSessionReady(ready: boolean): void {
    if (this.sessionReady === ready) return;
    this.sessionReady = ready;
    if (!ready) return;
    if (!this.pendingEstablish) this.pendingEstablish = { gen: this.currentGen, kind: 'establishment' };
    if (this.phase === 'awaiting_session') this.phase = 'awaiting_start';
    this.emit();
    this.maybeStart();
  }

  /**
   * An event happened. Newest-intent coalescing: a newer event REPLACES a pending one rather than
   * queueing a second probe — and it is never silently dropped, which was the original bug.
   */
  notifyEvent(reason: BonusEventReason): void {
    if (!this.active) return;
    this.startOrDeferEventProbe(reason);
  }

  /**
   * ⛔ THE single entry point for every event-driven read. Nothing else may store an external intent.
   *
   * It exists because the previous code stored the intent and called generic `maybeStart`, which left
   * an armed automatic `retryTimer` alive across the acceleration: the event probe would succeed and
   * the obsolete retry would still fire afterwards, spending rate budget on work whose reason had
   * ceased to exist and re-entering `establishing` after the owner was already bootstrapped.
   */
  private startOrDeferEventProbe(reason: BonusEventReason): void {
    // The event supersedes whatever the automatic ladder was waiting to retry. Cancel it — and do
    // NOT refund the attempt: the slot is consumed, so the ladder cannot be reset by event traffic.
    if (this.retryTimer !== null) this.automaticSlotsSpent += 1;
    this.clearRetryTimer();
    this.pendingExternal = { gen: this.currentGen, seq: this.currentSeq, reason };
    this.maybeStart();
  }

  // ── Starting ───────────────────────────────────────────────────────────────

  /**
   * Rungs of the automatic ladder CONSUMED this generation — by a real automatic start OR by a
   * cancellation. ⛔ ONE number decides both eligibility and delay, so a cancelled slot cannot be
   * refunded by any path: an event that cancels rung 1 makes the next automatic delay rung 2, and a
   * caller that cancels repeatedly exhausts the ladder instead of resetting backoff forever.
   * ⛔ The first ESTABLISHMENT read is rung 0 — it is not an automatic attempt and consumes nothing,
   * which is why every ladder index below is `rungsConsumed() + 1` against a 4-rung budget.
   */
  private rungsConsumed(): number {
    return this.automaticAttempts + this.automaticSlotsSpent;
  }

  /** Event probes obey the 1,500 ms floor. Lifecycle starts are deliberately NOT cooldown-gated. */
  private dueAt(kind: IntentKind): number {
    // ⛔ Keyed on the intent KIND, not on membership of a reason string in a set. A set lookup can
    // only ever describe the string it was handed; if a lifecycle string reaches the event door the
    // set faithfully reports "lifecycle" and hands back -Infinity. The kind is derived from WHICH
    // intent slot the read will consume, so it cannot disagree with what is about to be started.
    return kind === 'event' ? this.lastOwnerRequestStart + EVENT_MIN_INTERVAL_MS : -Infinity;
  }

  private mayStartNow(): boolean {
    return this.active && this.sessionReady && this.apiBase !== null && !this.inFlight;
  }

  /**
   * Which stored intent would a start satisfy? ⛔ Derived from STORED PRECEDENCE, never from a
   * caller-supplied reason — deciding from the caller is how a lifecycle start consumed an event
   * intent (and vice versa).
   */
  private effectiveIntentKind(): IntentKind | null {
    if (this.pendingEstablish) return this.pendingEstablish.kind;   // lifecycle OUTRANKS an event
    if (this.pendingExternal) return 'event';
    return null;
  }

  private maybeStart(): void {
    if (!this.mayStartNow()) return;
    // ⛔ ONE authority for the kind, used for the cooldown decision here and for the budget in
    // `dispatch()`. Both read the same stored intents with no mutation in between, so the read that
    // is gated and the read that is started can never be classified differently.
    const kind = this.effectiveIntentKind();
    if (kind === null) return;

    const due = this.dueAt(kind);
    const now = this.d.now();
    if (now < due) {
      // Defer, and arm the cooldown exactly once — never a second duplicate timer.
      if (this.cooldownTimer === null) {
        const armedGen = this.currentGen;
        this.cooldownTimer = this.d.setTimer(() => {
          this.cooldownTimer = null;
          // ⛔ Revalidate: a base change may have happened while this cooldown was armed.
          if (!this.active || armedGen !== this.currentGen) return;
          this.maybeStart();
        }, due - now);
      }
      this.phase = 'awaiting_start';
      this.emit();
      return;
    }
    this.dispatch();
  }

  private dispatch(): void {
    const base = this.apiBase;
    if (base === null) return;

    // ⛔ Consume by STORED PRECEDENCE, via the SAME helper that decided the cooldown. Lifecycle
    // establishment outranks an external intent when both exist; the event intent then survives to
    // produce its own trailing probe via settle row 4.
    const kind = this.effectiveIntentKind();
    if (kind === null) return;         // nothing to consume — never start a read with no intent
    if (this.pendingEstablish) this.pendingEstablish = null;
    else this.pendingExternal = null;

    // ⛔ Budgets are spent at ACTUAL START, by kind. Previously `automaticAttempts` incremented on
    // every failed settle, so a failed EVENT probe consumed the automatic ladder.
    if (kind === 'automatic') this.automaticAttempts += 1;
    else if (kind === 'event') this.eventProbes += 1;
    this.inFlightKind = kind;

    this.currentSeq += 1;
    const reqSeq = this.currentSeq;
    const reqGen = this.currentGen;
    this.inFlight = true;
    this.lastOwnerRequestStart = this.d.now();
    this.phase = 'establishing';
    this.emit();

    // ⛔ try/catch AND a rejection handler. A SYNCHRONOUS throw from `readOnce` never reaches
    // `.then`'s onRejected — it escapes the call entirely and leaves the owner parked in
    // `establishing` with no timer and no reply. Acceptance item 9 requires it handled.
    try {
      this.d.readOnce(base).then(
        (r) => this.onSettle(reqGen, reqSeq, r),
        () => this.onSettle(reqGen, reqSeq,
          { ok: false, observation: { kind: 'rejected' }, view: UNAVAILABLE_NETWORK }),
      );
    } catch {
      this.onSettle(reqGen, reqSeq,
        { ok: false, observation: { kind: 'threw' }, view: UNAVAILABLE_NETWORK });
    }
  }

  // ── Settle: the frozen 7 rows, in order ────────────────────────────────────

  private onSettle(reqGen: number, reqSeq: number, outcome: ReadOutcome): void {
    this.inFlight = false;

    // Row 1 — owner inactive: publish nothing, no timer, no trailing work.
    if (!this.active) return;

    // Row 2 — stale base: discard; if a current-gen intent is pending, start exactly one now that
    // the old request has actually settled.
    if (reqGen < this.currentGen) {
      if (this.pendingEstablish) this.maybeStart();
      else if (this.pendingExternal) this.maybeStart();
      return;
    }

    // Row 3 — superseded: non-current. Do NOT publish and do NOT move either latch; fall through
    // to row 4 for trailing handling only.
    if (reqSeq < this.currentSeq) {
      this.maybeStart();
      return;
    }

    // ⛔ Row 4 — TERMINAL, and it sits ABOVE publication BY DESIGN.
    //
    // A pending event means the owner already knows an answer-changing thing happened, so this
    // in-flight reply predates it. Publishing it first paints a pre-event `none` — telling a player
    // who just deposited that they have no bonus — and only then re-reads. The frozen settle order
    // is top-down for exactly this reason: row 4 starts the trailing probe and STOPS, and the
    // event probe's own reply is what publishes.
    if (this.pendingExternal) {
      this.maybeStart();
      return;
    }

    // ⛔ Row 5 keys on the DISCRIMINANT, not on a re-classification of the observation.
    // Deriving success from `classifyRead()` meant the `ok` flag was never consulted anywhere — a
    // mislabelled pair could publish a real view without bootstrapping, or bootstrap on an
    // unavailable one. A discriminant nothing narrows on is decoration.
    if (outcome.ok) {
      this.view = outcome.view;
      this.bootstrapped = true;
      this.phase = 'settled';
      // ⛔ NO counter reset here. The ladder is terminated by `bootstrapped`, never by zeroing a
      // budget: row 6 requires `!bootstrapped` and the retry callback returns early on it, so after
      // a success no automatic read can start no matter what the counters say. Zeroing them was
      // therefore behaviourally inert — but it read as a refund of spent rungs, which is exactly the
      // property `automaticSlotsSpent` exists to deny. The counters stay an honest per-generation
      // record, cleared only by `setBase()`, where the world genuinely changes.
      this.emit();
      if (this.pendingExternal) this.maybeStart();
      return;
    }

    // Failure arm ONLY. By type, `outcome.observation` here is Exclude<ReadObservation,{kind:'ok'}>,
    // so `classifyRead` can never return 'success' on this path.
    const cls: ReadClass = classifyRead(outcome.observation);
    this.inFlightKind = null;

    // Row 6 — automatic retry. ⛔ The `initialReadExhausted === false` conjunct is load-bearing:
    // without it, a transient failure on a post-exhaustion recovery probe would re-arm the ladder
    // and the visible exhaustion card would start flickering against a latch that is meant to hold.
    const mayRetry =
      isAutomaticallyRetryable(cls) &&
      this.rungsConsumed() + 1 < MAX_AUTOMATIC_ATTEMPTS &&
      !this.bootstrapped &&
      this.initialReadExhausted === false;

    if (mayRetry) {
      // attempts counts automatic reads STARTED, so the next rung is attempts + 1.
      const delay = nextAutomaticDelayMs(this.rungsConsumed() + 1);
      if (delay !== null) {
        this.view = outcome.view;
        this.phase = 'retry_scheduled';
        this.emit();
        this.clearRetryTimer();
        const armedGen = this.currentGen;
        this.retryTimer = this.d.setTimer(() => {
          this.retryTimer = null;
          // ⛔ REVALIDATE before starting. This timer was armed for a world that may no longer
          // exist: the owner may have been deactivated, the base changed, an event probe may have
          // already bootstrapped us, exhaustion may have latched, or the budget may be spent.
          // Starting unconditionally is how an obsolete retry fired after a successful event probe.
          if (!this.active) return;
          if (armedGen !== this.currentGen) return;
          if (this.bootstrapped) return;
          if (this.initialReadExhausted) return;
          if (this.rungsConsumed() >= MAX_AUTOMATIC_ATTEMPTS) return;
          this.pendingEstablish = { gen: this.currentGen, kind: 'automatic' };
          this.maybeStart();
        }, delay);
        return;
      }
    }

    // Row 7 — stop. Exhaustion rules.
    this.view = outcome.view;
    if (this.bootstrapped) {
      // A failure AFTER a real answer is genuine news: publish it over the retained view, still
      // visible, and arm NOTHING. A post-bootstrap failure is `settled`, never `exhausted`.
      this.phase = 'settled';
    } else {
      // Never had an answer: latch visible exhaustion. ⛔ No timer — and note a 429 lands here
      // precisely because `throttled` is not `transient`, so a rate limit is never answered with
      // more requests.
      this.initialReadExhausted = true;
      this.phase = 'exhausted';
    }
    this.emit();
    if (this.pendingExternal) this.maybeStart();
  }

  // ── Timers ─────────────────────────────────────────────────────────────────

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) { this.d.clearTimer(this.retryTimer); this.retryTimer = null; }
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimer !== null) { this.d.clearTimer(this.cooldownTimer); this.cooldownTimer = null; }
  }
}
